import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, User, Phone } from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

var CHANDRA_WA = "919910059861";
var DEEPIKA_WA = "919986023182";

function AbhinavAvatar() {
  return (
    <div className="w-8 h-8 rounded-full bg-[#2C39B8] flex items-center justify-center flex-shrink-0">
      <span className="text-white text-xs font-bold">A</span>
    </div>
  );
}

function WhatsAppButton({ number, name, leadInfo }) {
  var text = "Hi " + name + ", a visitor on ChannelLoyalty.ai would like to connect.";
  if (leadInfo.name) text += "\nName: " + leadInfo.name;
  if (leadInfo.company) text += "\nCompany: " + leadInfo.company;
  if (leadInfo.mobile) text += "\nMobile: " + leadInfo.mobile;
  text += "\nPlease take over the conversation.";
  var url = "https://wa.me/" + number + "?text=" + encodeURIComponent(text);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors mt-2"
      data-testid={"wa-connect-" + name.toLowerCase()}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.612.612l4.458-1.495A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.349 0-4.534-.683-6.381-1.858l-.445-.296-3.101 1.04 1.04-3.101-.296-.445A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
      Chat with {name}
    </a>
  );
}

// Lightweight markdown renderer: **bold** → <strong>, keeps newlines via pre-wrap
function renderMarkdownLite(text) {
  var parts = text.split(/(\*\*[^*]+\*\*)/g);
  var out = [];
  for (var i = 0; i < parts.length; i++) {
    var part = parts[i];
    if (part.length > 4 && part.indexOf('**') === 0 && part.lastIndexOf('**') === part.length - 2) {
      out.push(<strong key={i}>{part.slice(2, -2)}</strong>);
    } else if (part) {
      out.push(part);
    }
  }
  return out;
}

function MessageBubble({ msg, leadInfo }) {
  var isUser = msg.role === 'user';
  var content = msg.content || '';

  // Parse handover tags
  var hasHandover = content.includes('[HANDOVER:');
  var cleanContent = content.replace(/\[HANDOVER:(chandra|deepika)\]/gi, '').trim();

  return (
    <div className={"flex gap-2 " + (isUser ? "justify-end" : "justify-start")}>
      {!isUser && <AbhinavAvatar />}
      <div className={isUser ? "max-w-[75%]" : "max-w-[80%]"}>
        <div
          className={
            isUser
              ? "bg-[#2C39B8] text-white rounded-2xl rounded-br-sm px-4 py-2.5"
              : "bg-gray-100 text-gray-800 rounded-2xl rounded-bl-sm px-4 py-2.5"
          }
        >
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{isUser ? cleanContent : renderMarkdownLite(cleanContent)}</p>
        </div>
        {hasHandover && (
          <div className="flex flex-wrap gap-2 mt-2">
            {content.toLowerCase().includes('[handover:chandra]') && (
              <WhatsAppButton number={CHANDRA_WA} name="Chandra" leadInfo={leadInfo} />
            )}
            {content.toLowerCase().includes('[handover:deepika]') && (
              <WhatsAppButton number={DEEPIKA_WA} name="Deepika" leadInfo={leadInfo} />
            )}
            {!content.toLowerCase().includes('[handover:chandra]') && !content.toLowerCase().includes('[handover:deepika]') && (
              <>
                <WhatsAppButton number={CHANDRA_WA} name="Chandra" leadInfo={leadInfo} />
                <WhatsAppButton number={DEEPIKA_WA} name="Deepika" leadInfo={leadInfo} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export var ChatWidget = function ChatWidget() {
  var openState = useState(false);
  var isOpen = openState[0];
  var setIsOpen = openState[1];

  var msgsState = useState([]);
  var messages = msgsState[0];
  var setMessages = msgsState[1];

  var inputState = useState('');
  var input = inputState[0];
  var setInput = inputState[1];

  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var sessionState = useState(null);
  var sessionId = sessionState[0];
  var setSessionId = sessionState[1];

  var leadState = useState({ name: null, company: null, mobile: null });
  var leadInfo = leadState[0];
  var setLeadInfo = leadState[1];

  var hasAutoOpened = useRef(false);
  var messagesEndRef = useRef(null);
  var visitorId = useRef('v-' + Date.now() + '-' + Math.random().toString(36).substr(2, 6));

  // Auto-popup after 4 seconds
  useEffect(function() {
    var timer = setTimeout(function() {
      if (!hasAutoOpened.current) {
        hasAutoOpened.current = true;
        setIsOpen(true);
        setMessages([{
          role: 'assistant',
          content: "Hi there! I'm the ChannelLoyalty AI assistant. Whether you're looking to reduce dealer churn, engage influencers, or build a loyalty program for your channel partners — I can help.\n\nOur senior loyalty architects Chandra and Deepika are also available if you'd like a personalized conversation.\n\nWhat industry are you in, and what brings you here today?",
        }]);
      }
    }, 4000);
    return function() { clearTimeout(timer); };
  }, []);

  // Scroll to bottom
  useEffect(function() {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  var handleSend = useCallback(async function() {
    if (!input.trim() || loading) return;
    var userMessage = input.trim();
    setInput('');
    setMessages(function(prev) { return prev.concat([{ role: 'user', content: userMessage }]); });
    setLoading(true);

    // Add streaming assistant message placeholder
    var assistantIdx = messages.length + 1;
    setMessages(function(prev) { return prev.concat([{ role: 'assistant', content: '', streaming: true }]); });

    try {
      var response = await fetch(API_URL + '/api/chat/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          visitor_id: visitorId.current,
          message: userMessage,
        }),
      });

      var reader = response.body.getReader();
      var decoder = new TextDecoder();
      var fullText = '';
      var buffer = '';

      while (true) {
        var result = await reader.read();
        if (result.done) break;
        buffer += decoder.decode(result.value, { stream: true });

        var lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (var i = 0; i < lines.length; i++) {
          var line = lines[i].trim();
          if (!line.startsWith('data: ')) continue;
          try {
            var data = JSON.parse(line.substring(6));
            if (data.type === 'delta') {
              fullText += data.content;
              setMessages(function(prev) {
                var updated = prev.slice();
                var last = updated[updated.length - 1];
                if (last && last.role === 'assistant') {
                  updated[updated.length - 1] = { role: 'assistant', content: fullText };
                }
                return updated;
              });
            } else if (data.type === 'done') {
              if (data.session_id) setSessionId(data.session_id);
              if (data.lead_info) setLeadInfo(data.lead_info);
            }
          } catch (e) { /* skip parse errors */ }
        }
      }
    } catch (err) {
      console.error('Chat stream error:', err);
      setMessages(function(prev) {
        var updated = prev.slice();
        var last = updated[updated.length - 1];
        if (last && last.role === 'assistant' && !last.content) {
          updated[updated.length - 1] = { role: 'assistant', content: 'I apologize for the brief interruption. How can I help you with channel loyalty today?' };
        }
        return updated;
      });
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId, messages.length]);

  var handleKeyDown = function(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  var handleOpen = function() {
    setIsOpen(true);
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hi there! I'm the ChannelLoyalty AI assistant. Whether you're looking to reduce dealer churn, engage influencers, or build a loyalty program — I can help.\n\nOur senior architects Chandra and Deepika are also available on WhatsApp.\n\nWhat industry are you in?",
      }]);
    }
  };

  return (
    <div className="chat-widget" data-testid="chat-widget">
      {isOpen && (
        <div className="chat-window mb-4 flex flex-col animate-slide-up" style={{ position: 'relative', zIndex: 60, maxHeight: '520px' }}>
          {/* Header */}
          <div className="bg-[#2C39B8] p-3 flex items-center gap-3" style={{ borderRadius: '16px 16px 0 0' }}>
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <User size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-sm">ChannelLoyalty</h3>
              <p className="text-green-300 text-xs flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block"></span>
                Chandra & Deepika • Online
              </p>
            </div>
            <button onClick={function() { setIsOpen(false); }} className="text-white/70 hover:text-white p-1" aria-label="Close" data-testid="chat-close">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-white" style={{ minHeight: '280px', maxHeight: '360px' }}>
            {messages.map(function(msg, idx) {
              return <MessageBubble key={idx} msg={msg} leadInfo={leadInfo} />;
            })}
            {loading && messages.length > 0 && messages[messages.length - 1].role !== 'assistant' && (
              <div className="flex gap-2 justify-start">
                <AbhinavAvatar />
                <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
                  <Loader2 className="w-4 h-4 animate-spin text-[#2C39B8]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-100 bg-white" style={{ borderRadius: '0 0 16px 16px' }}>
            <div className="flex gap-2">
              <input
                value={input}
                onChange={function(e) { setInput(e.target.value); }}
                onKeyDown={handleKeyDown}
                placeholder="Ask about channel loyalty..."
                className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2C39B8]/30 focus:border-[#2C39B8]"
                data-testid="chat-input"
              />
              <button
                onClick={handleSend}
                disabled={loading || !input.trim()}
                className="bg-[#2C39B8] hover:bg-[#1F2C99] disabled:opacity-40 text-white rounded-xl px-3 py-2.5 transition-colors"
                data-testid="chat-send-btn"
              >
                <Send size={16} />
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5 text-center">Powered by ChannelLoyalty.ai</p>
          </div>
        </div>
      )}

      {/* Chat Bubble */}
      <button
        onClick={isOpen ? function() { setIsOpen(false); } : handleOpen}
        className="chat-bubble"
        data-testid="chat-toggle"
        aria-label="Chat with ChannelLoyalty"
        style={{ position: 'relative', zIndex: 55 }}
      >
        {isOpen ? <X size={24} className="text-white" /> : <MessageCircle size={24} className="text-white" />}
      </button>
    </div>
  );
};
